export class PoolStorage<Entity> {
  private reservedEntities: Map<string, Entity>;

  private entitiesPool: Entity[];

  constructor(initialEntitiesPool?: Entity[]) {
    this.reservedEntities = new Map();
    this.entitiesPool = initialEntitiesPool ?? [];
  }

  getEntity(entityId: string): Entity {
    const entity = this.reservedEntities.get(entityId);

    if (!entity) {
      throw new Error(`could not find entity with id: ${entityId}`);
    }

    return entity;
  }

  updateEntities(data: Record<string, Partial<Entity>>) {
    const notFoundEntityIds: string[] = [];
    for (const [entityId, valueToUpdate] of Object.entries(data)) {
      const entity = this.reservedEntities.get(entityId);
      if (entity) {
        const updatedEntity = { ...entity, ...valueToUpdate };

        this.reservedEntities.set(entityId, updatedEntity);
      } else {
        notFoundEntityIds.push(entityId);
      }
    }

    if (notFoundEntityIds.length > 0) {
      throw new Error(
        `Failed to update some entities: [${notFoundEntityIds.join(
          ', ',
        )}] – not found`,
      );
    }
  }

  async create(
    entityIds: string[],
    factoryMethod: (amount: number) => Promise<Entity[]>,
  ): Promise<Record<string, Entity>> {
    const amount = entityIds.length;
    const resultPullItems: Record<string, Entity> = {};
    const initialPoolLength = this.entitiesPool.length;
    const currentEntityIds = [...entityIds];

    while (this.entitiesPool.length > 0 && currentEntityIds.length > 0) {
      if (Object.values(resultPullItems).length === amount) {
        break;
      }

      const currentEntityId = currentEntityIds.pop()!;
      const poolEntity = this.entitiesPool.pop()!;
      resultPullItems[currentEntityId] = poolEntity;
      this.reservedEntities.set(currentEntityId, poolEntity);
    }

    if (initialPoolLength < amount) {
      const createdEntities = await factoryMethod(amount - initialPoolLength);

      createdEntities.forEach((entity: Entity) => {
        const currentEntityId = currentEntityIds.pop()!;
        this.reservedEntities.set(currentEntityId, entity);
        resultPullItems[currentEntityId] = entity;
      });
    }

    return resultPullItems;
  }

  free(entityIds: string[]): void {
    entityIds.forEach((entityId) => {
      const entity = this.reservedEntities.get(entityId);
      if (entity) {
        this.entitiesPool.push(entity);
        this.reservedEntities.delete(entityId);
      }
    });
  }

  freeAll(): void {
    this.reservedEntities.forEach((entity, entityId) => {
      this.entitiesPool.push(entity);
      this.reservedEntities.delete(entityId);
    });
  }

  flush(flushMethod: (entity: Entity) => void): void {
    this.entitiesPool.forEach((entity) => {
      flushMethod(entity);
    });
    this.entitiesPool = [];

    Array.from(this.reservedEntities.values()).forEach((entity) => {
      flushMethod(entity);
    });
    this.reservedEntities.clear();
  }
}
